import { Controller } from "@hotwired/stimulus"
import { getCsrfToken } from "shared/form_helpers"
import { sendSuccessNotification, sendFailureNotification } from "shared/toast_notifications"

// Connects to data-controller="settings--models-table"
export default class extends Controller {
    static targets = [
        "table",
        "createModal",
        "createForm",
        "nameInput",
        "costInput",
        "parentIdInput",
        "editModal",
        "editForm",
        "editNameInput",
        "editCostInput",
        "editIdInput"
    ]

    static values = {
        deviceTypeId: String,
        manufacturerId: String
    }

    connect() {
        this.initializeTable()
        this.loadModels()
    }

    initializeTable() {
        // No initialization needed — argus--table controller handles this
    }

    _getArgusTable() {
        if (!this.hasTableTarget) return null
        return this.application.getControllerForElementAndIdentifier(
            this.tableTarget, 'argus--table'
        )
    }

    async loadModels() {
        if (!this.deviceTypeIdValue || !this.manufacturerIdValue) {
            console.error('No device type ID or manufacturer ID provided')
            return
        }

        const argusTable = this._getArgusTable()
        argusTable?.showLoading()
        try {
            const response = await fetch(`/api/device_types/device_model/list?device_type_id=${this.deviceTypeIdValue}&manufacturer_id=${this.manufacturerIdValue}`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                }
            })

            if (!response.ok) {
                throw new Error('Failed to fetch models')
            }

            const data = await response.json()
            this.populateTable(data.models || [])
        } catch (error) {
            console.error('Error loading models:', error)
            sendFailureNotification('Failed to load models')
        } finally {
            argusTable?.hideLoading()
        }
    }

    populateTable(models) {
        if (this.hasTableTarget) {
            const argusTable = this.application.getControllerForElementAndIdentifier(
                this.tableTarget, 'argus--table'
            )
            if (argusTable) {
                argusTable.loadData(models)
            }
        }
    }

    openCreateModal() {
        // Clear form
        if (this.hasNameInputTarget) {
            this.nameInputTarget.value = ''
        }
        if (this.hasCostInputTarget) {
            this.costInputTarget.value = ''
        }
        
        // Set parent ID (manufacturer ID)
        if (this.hasParentIdInputTarget) {
            this.parentIdInputTarget.value = this.manufacturerIdValue
        }

        // Show modal
        const modal = bootstrap.Modal.getOrCreateInstance(this.createModalTarget)
        modal.show()
    }

    async handleCreateSubmit(event) {
        event.preventDefault()

        const formData = new FormData(this.createFormTarget)

        try {
            const response = await fetch('/api/device_types/device_model/create', {
                method: 'POST',
                headers: {
                    'X-CSRF-Token': getCsrfToken()
                },
                body: formData
            })

            if (!response.ok) {
                throw new Error('Failed to create model')
            }

            // Close modal and wait for it to fully hide before proceeding
            await new Promise(resolve => {
                const modal = bootstrap.Modal.getInstance(this.createModalTarget)
                if (modal) {
                    this.createModalTarget.addEventListener('hidden.bs.modal', () => {
                        modal.dispose()
                        resolve()
                    }, { once: true })
                    modal.hide()
                } else {
                    resolve()
                }
            })

            // Reload table
            await this.loadModels()

            sendSuccessNotification('Model created successfully')
        } catch (error) {
            console.error('Error creating model:', error)
            sendFailureNotification('Failed to create model')
        }
    }

    openEditModal(event) {
        event.preventDefault()

        const button = event.currentTarget
        const modelId = button.dataset.modelId

        // Fetch model details
        this.fetchModelDetails(modelId)
    }

    async fetchModelDetails(modelId) {
        try {
            const response = await fetch(`/api/device_types/device_model/describe?id=${modelId}`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                }
            })

            if (!response.ok) {
                throw new Error('Failed to fetch model details')
            }

            const data = await response.json()
            const model = data.device_type_definitions[0]

            // Populate edit form
            if (this.hasEditNameInputTarget) {
                this.editNameInputTarget.value = model.name || ''
            }
            if (this.hasEditCostInputTarget) {
                this.editCostInputTarget.value = model.cost || ''
            }
            if (this.hasEditIdInputTarget) {
                this.editIdInputTarget.value = model.id
            }

            // Show modal
            const modal = bootstrap.Modal.getOrCreateInstance(this.editModalTarget)
            modal.show()
        } catch (error) {
            console.error('Error fetching model details:', error)
            sendFailureNotification('Failed to load model details')
        }
    }

    async handleEditSubmit(event) {
        event.preventDefault()

        const formData = new FormData(this.editFormTarget)

        try {
            const response = await fetch('/api/device_types/device_model/update', {
                method: 'PATCH',
                headers: {
                    'X-CSRF-Token': getCsrfToken()
                },
                body: formData
            })

            if (!response.ok) {
                throw new Error('Failed to update model')
            }

            // Close modal and wait for it to fully hide before proceeding
            await new Promise(resolve => {
                const modal = bootstrap.Modal.getInstance(this.editModalTarget)
                if (modal) {
                    this.editModalTarget.addEventListener('hidden.bs.modal', () => {
                        modal.dispose()
                        resolve()
                    }, { once: true })
                    modal.hide()
                } else {
                    resolve()
                }
            })

            // Reload table
            await this.loadModels()

            sendSuccessNotification('Model updated successfully')
        } catch (error) {
            console.error('Error updating model:', error)
            sendFailureNotification('Failed to update model')
        }
    }

    async deleteModel(event) {
        event.preventDefault()

        const button = event.currentTarget
        const modelId = button.dataset.modelId

        // Get model name from table row
        const row = button.closest('tr')
        const nameCell = row.querySelector('td:first-child')
        const modelName = nameCell ? nameCell.textContent : 'this model'

        if (!confirm(`Are you sure you want to delete ${modelName}?`)) {
            return
        }

        const formData = new FormData()
        formData.append('id', modelId)
        formData.append('name', modelName)

        try {
            const response = await fetch('/api/device_types/device_model/delete', {
                method: 'DELETE',
                headers: {
                    'X-CSRF-Token': getCsrfToken()
                },
                body: formData
            })

            if (!response.ok) {
                throw new Error('Failed to delete model')
            }

            // Reload table
            await this.loadModels()

            sendSuccessNotification('Model deleted successfully')
        } catch (error) {
            console.error('Error deleting model:', error)
            sendFailureNotification('Failed to delete model')
        }
    }
}
